import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { UnidataTable } from '~/components';
import {
  DESCARGA_NF,
  SOBRAS_E_FALTAS,
  TELEMETRIA,
  CUSTOMIZACAO_RAIZEN,
} from '~/constants/modules';
import { t, can, number } from '~/helpers';
import { getToastErrors } from '~/helpers/errorsRequest';
import { handleExportGeneric } from '~/helpers/exportReports';
import api from '~/services/api';
import { resetParams } from '~/store/modules/hooks/actions';
import {
  deleteTanqueFisico,
  findTanqueFisico,
  recordFiltering,
} from '~/store/modules/tanquefisico/actions';

import Form from './form';

function TanqueFisicoList() {
  const [probes, setProbes] = useState([]);

  const redux = useSelector((state) => state.tanquefisico);
  const dispatch = useDispatch();

  // Opções status
  const status = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'B', value: t('s.geral.opcoes.bloqueado') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  // Header
  const header = useMemo(() => {
    const data = [
      {
        id: 'codigotanquefisico',
        label: t('s.configuracao.tanquefisico.codigotanquefisico'),
        filter: { type: 'text' },
      },
      {
        id: 'codigonomeposto',
        label: t('s.geral.postocomboio'),
        filter: { type: 'text' },
      },
      {
        id: 'codigoauxiliartanquefisico',
        label: t('s.configuracao.tanquefisico.codigoauxiliartanquefisico'),
        filter: { type: 'text' },
      },
      {
        id: 'capacidadetanquefisico',
        label: t('s.configuracao.tanquefisico.capacidadetanquefisico'),
        align: 'right',
        formatted: (value) => number(value),
      },
      {
        id: 'volumeoperacionaltanquefisico',
        label: t('s.configuracao.tanquefisico.volumeperacionaltanquefisico'),
        align: 'right',
        formatted: (value) => number(value),
      },
      {
        id: 'statustanquefisico',
        label: t('s.configuracao.tanquefisico.statustanquefisico'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: status },
      },
    ];

    if (can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) {
      data.splice(1, 0, {
        id: 'idtanquevirtual',
        label: t('s.configuracao.tanquefisico.idtanquevirtual'),
        filter: { type: 'text' },
      });
    }

    if (can(TELEMETRIA)) {
      data.splice(4, 0, {
        id: 'tiposondatanquefisico',
        label: t('s.configuracao.tanquefisico.tiposondatanquefisico'),
        formatted: (v) => probes.find((p) => p.label === v)?.value || '',
        filter: { type: 'select', options: probes },
      });
    }

    if (!(can(DESCARGA_NF) || can(SOBRAS_E_FALTAS))) {
      data.splice(5, 0, {
        id: 'nomeproduto',
        label: t('s.configuracao.tanquefisico.nomeabreviadoproduto'),
        filter: { type: 'text' },
      });
    }

    return data;
  }, [status, probes]);

  const isResponseProbes = useCallback(async () => {
    try {
      await api.get('/driverstanques').then(({ data }) => {
        setProbes(
          data.items.map((probe) => ({
            label: probe.iddriversonda,
            value: probe.nomedriversonda,
          }))
        );
      });
    } catch (error) {
      getToastErrors(error);
    }
  }, []);

  useEffect(() => {
    isResponseProbes();
    dispatch(resetParams());
  }, []);

  // impressao pdf ou excel

  const fieldlist = ['codigotanquefisico'];

  if (can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) {
    fieldlist.push('idtanquevirtual');
  }

  if (can(TELEMETRIA) || can(DESCARGA_NF) || can(SOBRAS_E_FALTAS)) {
    fieldlist.push('nomearqueacaotanquefisico');
  }

  if (!(can(DESCARGA_NF) || can(SOBRAS_E_FALTAS))) {
    fieldlist.push('nomeproduto');
  }

  fieldlist.push('codigonomeposto', 'codigoauxiliartanquefisico');

  if (can(CUSTOMIZACAO_RAIZEN)) {
    fieldlist.push('codigocosantanquefisico');
  }

  if (can(TELEMETRIA)) {
    fieldlist.push('nometiposondatanquefisico');
  }

  fieldlist.push(
    'capacidadetanquefisico',
    'capacidadeoperacionaltanquefisico',
    'volumeoperacionaltanquefisico'
  );

  if (can(TELEMETRIA)) {
    fieldlist.push(
      'numerosondatanquefisico',
      'enderecosondatanquefisico',
      'portasocketsondatanquefisico',
      'deltatanquefisico',
      'deltaaguatanquefisico',
      'limiaraguatanquefisico',
      'alarmenivelmuitobaixotanquefisico',
      'alarmenivelbaixotanquefisico',
      'alarmenivelaltotanquefisico',
      'alarmenivelmuitoaltotanquefisico',
      'variacaonivelturnofechadotanquefisico'
    );
  }

  fieldlist.push('tipotanquefisico', 'statustanquefisico');

  const substituirHeader = {
    codigotanquefisico: t('s.configuracao.tanquefisico.codigotanquefisico'),
    codigonomeposto: t('s.geral.postocomboio'),
    codigoauxiliartanquefisico: t(
      's.configuracao.tanquefisico.codigoauxiliartanquefisico'
    ),
    capacidadetanquefisico: t(
      's.configuracao.tanquefisico.capacidadetanquefisico'
    ),
    capacidadeoperacionaltanquefisico: t(
      's.cadastro.veiculo.volumetanqueveiculo'
    ),
    volumeoperacionaltanquefisico: t('s.relatorio.afericao.volume'),
    statustanquefisico: t('s.configuracao.tanquefisico.statustanquefisico'),

    alarmenivelmuitobaixotanquefisico: t(
      's.configuracao.tanquefisico.alarmenivelmuitobaixotanquefisico'
    ),
    alarmenivelbaixotanquefisico: t(
      's.configuracao.tanquefisico.alarmenivelbaixotanquefisico'
    ),
    alarmenivelaltotanquefisico: t(
      's.configuracao.tanquefisico.alarmenivelaltotanquefisico'
    ),
    alarmenivelmuitoaltotanquefisico: t(
      's.configuracao.tanquefisico.alarmenivelmuitoaltotanquefisico'
    ),
    variacaonivelturnofechadotanquefisico: t(
      's.configuracao.tanquefisico.variacaonivelturnofechadotanquefisico'
    ),
    numerosondatanquefisico: t(
      's.configuracao.tanquefisico.numerosondatanquefisico'
    ),
    enderecosondatanquefisico: t(
      's.configuracao.tanquefisico.enderecosondatanquefisico'
    ),
    portasocketsondatanquefisico: t(
      's.configuracao.tanquefisico.portasocketsondatanquefisico'
    ),
    tiposondatanquefisico: t(
      's.configuracao.tanquefisico.tiposondatanquefisico'
    ),
    deltatanquefisico: t('s.configuracao.tanquefisico.deltatanquefisico'),
    deltaaguatanquefisico: t(
      's.configuracao.tanquefisico.deltaaguatanquefisico'
    ),
    limiaraguatanquefisico: t(
      's.configuracao.tanquefisico.limiaraguatanquefisico'
    ),
    limiarprodutotanquefisico: t(
      's.configuracao.tanquefisico.limiarprodutotanquefisico'
    ),
    codigocosantanquefisico: t(
      's.configuracao.tanquefisico.codigocosantanquefisico'
    ),
    nomeproduto: t('s.menu.configuracao.produto'),
    nomearqueacaotanquefisico: t('s.menu.configuracao.arqueacao'),
    tipotanquefisico: t('s.configuracao.tanquefisico.tipotanquefisico'),
    nometiposondatanquefisico: t(
      's.configuracao.tanquefisico.tiposondatanquefisico'
    ),
    idtanquevirtual: t('s.menu.configuracao.tanquevirtual'),
  };

  return (
    <UnidataTable
      fixed
      redux={redux}
      header={header}
      read={recordFiltering}
      find={findTanqueFisico}
      remove={deleteTanqueFisico}
      form={Form}
      keys={['idtanquefisico', 'idposto']}
      detach="idposto"
      excel
      pdf
      onExport={(formato) =>
        handleExportGeneric({
          endpoint: 'tanquesfisicos',
          formato,
          fields: fieldlist,
          redux,
          headers: substituirHeader,
          title: 'Relatório de Tanques Físicos',
          ordination: redux.params.ordination,
          orderfield: redux.params.orderfield,
          substituirValores: {
            statustanquefisico: { L: 'Liberado', B: 'Bloqueado' },
            tiposondatanquefisico: {
              '0': 'Manual',
              '1': 'MTS',
              '2': 'Veeder-Root',
              '3': 'Radar',
              '4': 'LLS',
              '5': 'Veeder-Root ASCII',
              '6': 'Fafnir',
              '7': 'MTS Modbus',
              '8': 'Fafnir Modbus',
              '9': 'Veeder-Root Client',
              '10': 'Fafnir Modbus RTU',
              '11': 'Eztech Cliente MQTT',
              '12': 'Telemed',
              '14': 'Veeder-Root Server (local)',
            },
          },
        })
      }
    />
  );
}

export default TanqueFisicoList;
